/* eslint-disable */
import { useEffect, useRef, useState } from 'react'
import { ApiResponse, NormalizedLocationData } from './types'
import {
  dispatchMyLocationCookieUpdated,
  MY_LOCATION_COOKIE_UPDATED_EVENT,
  type MyLocationCookieUpdatedDetail
} from './my-location-cookie-updated-event'
import {
  getCommercializationData,
  getInitialLocationFromCookies,
  getLocationData,
  saveLocalizationCookie
} from './utils'

export const useMyLocation = () => {
  const { zipcode: intialZipcode, statecode: initialStateCode } =
    getInitialLocationFromCookies()
  const [zipcode, setZipcode] = useState<string | null>(intialZipcode)
  const [statecode, setStatecode] = useState<string | null>(initialStateCode)
  const [isCommercialized, setIsCommercialized] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  let commercializationData = useRef<ApiResponse | null>(null)

  useEffect(() => {
    if (!statecode) return

    const stateSearchTerm = `US-${statecode.toUpperCase()}`

    const getAndStoreData = async () => {
      if (commercializationData.current) return commercializationData.current
      try {
        const data = await getCommercializationData()
        commercializationData.current = data
        return data
      } catch (e) {
        console.error('Error fetching commercialization statuses:', e)
        return null
      }
    }

    const setCommercializationStatus = async () => {
      const commData = await getAndStoreData()

      if (!commData) {
        return
      }

      const commercializedStates = commData.commercializationStatuses
        .filter((stateEntry) => stateEntry.status === 'COMMERCIALIZED')
        .map((stateEntry) => stateEntry.state)

      const isCommercialized = commercializedStates.includes(stateSearchTerm)
      setIsCommercialized(isCommercialized)
    }

    setCommercializationStatus()
  }, [statecode])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {}
    }
    const syncFromCookies = (event: Event) => {
      const { detail } = event as CustomEvent<MyLocationCookieUpdatedDetail>
      if (detail) {
        setZipcode(detail.zipcode)
        setStatecode(detail.statecode)
        return
      }
      const localizationCookie = getInitialLocationFromCookies()
      setZipcode(localizationCookie.zipcode)
      setStatecode(localizationCookie.statecode)
    }
    window.addEventListener(MY_LOCATION_COOKIE_UPDATED_EVENT, syncFromCookies)
    return () => {
      window.removeEventListener(
        MY_LOCATION_COOKIE_UPDATED_EVENT,
        syncFromCookies
      )
    }
  }, [])

  const handleNewLocalizationData = (
    localizationData: NormalizedLocationData
  ) => {
    // Avoid redundant state updates when location data hasn't actually changed.
    setZipcode((prev) =>
      prev === localizationData.zip ? prev : localizationData.zip
    )
    setStatecode((prev) =>
      prev === localizationData.stateShortname
        ? prev
        : localizationData.stateShortname
    )
    saveLocalizationCookie(localizationData)
    dispatchMyLocationCookieUpdated({
      zipcode: localizationData.zip,
      statecode: localizationData.stateShortname
    })
    setIsLoading(false)
  }

  const applyNavigatorLocation: () => Promise<{
    success: boolean
  }> = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.')
      return { success: false }
    }

    setIsLoading(true)

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject)
        }
      )
      const localizationData = await getLocationData({ useGeoLocator: true })
      if (
        !localizationData ||
        localizationData.countryCode.toUpperCase() !== 'US'
      ) {
        return { success: false }
      }

      handleNewLocalizationData(localizationData)
      return { success: true }
    } catch (e) {
      alert(
        'Unable to retrieve your location. Please grant permission and try again.'
      )
    } finally {
      setIsLoading(false)
    }

    return { success: false }
  }

  const applyLocationFromInput: (zip: string) => Promise<{
    success: boolean
  }> = async (zip) => {
    setIsLoading(true)
    const localizationData = await getLocationData({ zipcode: zip })
    if (localizationData) {
      handleNewLocalizationData(localizationData)
      return { success: true }
    }
    setIsLoading(false)

    return { success: false }
  }

  return {
    zipcode,
    statecode,
    setZipcode,
    setStatecode,
    isCommercialized,
    isLoading,
    applyNavigatorLocation,
    applyLocationFromInput,
    commercializationData: commercializationData.current
  }
}
